import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { compact, sortBy } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import { MiniSlider } from 'components/controls/MiniSlider';
import {
	getItemAlteredByScenario,
	vulnerabilityIndicators
} from 'utils/rules';
import HorizontalVulnerabilityScale from 'components/Radar/HorizontalVulnerabilityScale';

import Table from 'components/Table';
import { Button, Tooltip } from 'antd';
import Weight from 'components/controls/Weight';

import dayjs from 'dayjs';
import { IndicatorModel } from 'model/IndicatorModel';

import './IndicatorsList.sass';

interface ExtendedIndicatorModel extends IndicatorModel {
	index: number;
}

interface IndicatorsListProps {
	// List of indicators to display
	indicators: Array<IndicatorModel>;

	// List of available scenarios
	scenarios: Array<{
		id: string;
		name: string;
	}>;

	// Currently selected scenario
	scenario: {
		id: string;
		name?: string;
	} | null;

	// Callback function for updating an indicator
	onUpdateIndicator: (id: string, values: {
		trend?: string;
		weight?: number;
	}) => void;

	// Callback function for editing an indicator
	onEditIndicator: (indicator: IndicatorModel) => void;

	// Option to disable interactions
	visibleColumns?: {
		edit?: boolean;
		id?: boolean;
		evaluatedAt?: boolean;
		name?: boolean;
		status?: boolean;
		weight?: boolean;
	};

	readOnly?: boolean;
}

export const IndicatorsList = (props: IndicatorsListProps) => {
	const { t } = useTranslation();

	const {
		indicators = [],
		visibleColumns = {
			edit: true,
			id: true,
			evaluatedAt: true,
			name: true,
			status: true,
			weight: true
		},
		readOnly = false
	} = props;

	const allScenarios = (props.scenarios || []).concat([{ id: '', name: t('models.scenario.baseline') }]);
	let alteredIndicators = sortBy(
		indicators.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index,
			key: c.id || `indicator-${index}` // Add a unique key for each element
		})
		),
		'name'
	) as ExtendedIndicatorModel[];

	let vulnerabilities = (props.scenarios || []).map((sc) => {
		return {
			id: sc.id,
			name: sc.name,
			vulnerability: vulnerabilityIndicators(
				indicators.map((c) => getItemAlteredByScenario(c, sc))
			)
		};
	});

	vulnerabilities.push({
		id: '',
		name: t('models.scenario.baseline'),
		vulnerability: vulnerabilityIndicators(indicators)
	});

	const columns = compact([
		visibleColumns['edit'] && {
			title: '',
			dataIndex: 'edit',
			key: 'edit',
			width: 50,
			render: (text: string, record: ExtendedIndicatorModel) => (
				<Tooltip title='Edit'>
					<Button
						shape="circle"
						disabled={!!props.scenario?.id}
						style={{ opacity: 1 }}
						onClick={
							!!props.scenario?.id
								? () => { }
								: () => {
									props.onEditIndicator(record);
								}
						}
					>
						<FontAwesomeIcon
							icon={faEdit as IconDefinition}
							style={{
								cursor: !!props.scenario?.id
									? 'not-allowed'
									: 'initial',
								opacity: !!props.scenario?.id ? 0.5 : 1
							}}
						/>
					</Button>
				</Tooltip>
			)
		},
		visibleColumns['id'] && {
			title: '#',
			dataIndex: 'id',
			key: 'id',
			defaultSortOrder: 'ascend',
			width: 60,
			sortable: (a: ExtendedIndicatorModel, b: ExtendedIndicatorModel) => {
				const scoreA = (a.reference || ("I" + (a.index + 1))).toLocaleLowerCase()
				const scoreB = (b.reference || ("I" + (b.index + 1))).toLocaleLowerCase()

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record: ExtendedIndicatorModel) => {
				return record.reference || ("I" + ((record.index + 1 + "").padStart(2, '0')))
			}
		},
		visibleColumns['evaluatedAt'] && {
			title: t("models.indicator.fields.evaluated_at"),
			dataIndex: 'evaluatedAt',
			key: 'evaluatedAt',
			className: 'nowrap',
			width: 140,
			sortable: (a: ExtendedIndicatorModel, b: ExtendedIndicatorModel) => {
				if (!b.evaluatedAt) return -1
				if (!a.evaluatedAt) return 1
				if (a.evaluatedAt && b.evaluatedAt && a.evaluatedAt < b.evaluatedAt) return -1;
				if (a.evaluatedAt && b.evaluatedAt > b.evaluatedAt) return 1;
				return 0
			},
			render: (text: string, record: ExtendedIndicatorModel, index: number) => {
				return <Tooltip title={record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY HH[h]mm') : ''}>
					<div>
						{record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY') : ''}
					</div>
				</Tooltip>
			}
		},
		visibleColumns['name'] && {
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			className: 'w-50',
			sortable: true,
			ellipsis: true,
			render: (text: string, record: ExtendedIndicatorModel) => {
				return (
					<div className="name w-100">
						{record.name}
						{record.description ? (
							<div className="description">
								{record.description}
							</div>
						) : (
							''
						)}
					</div>
				);
			}
		},
		visibleColumns['status'] && {
			title: 'Status',
			dataIndex: 'trend',
			key: 'trend',
			width: 200,
			sortable: (a: ExtendedIndicatorModel, b: ExtendedIndicatorModel) => {
				const scoreA = parseInt(a.trend)
				const scoreB = parseInt(b.trend)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record: ExtendedIndicatorModel) => {
				return (
					<MiniSlider
						className="mr-2"
						key={record.id}
						value={record.trend}
						min={0}
						max={100}
						style={{ width: '100%', flexShrink: 0 }}
						onChange={(val: string) =>
							props.onUpdateIndicator(record.id, { trend: val })
						}
						readOnly={readOnly}
						additionalMarkers={(allScenarios).map((sc) => {
							return {
								id: sc.id,
								name: sc.name || t("models.scenario.baseline"),
								value: getItemAlteredByScenario(record, sc).trend
							};
						})}
					/>
				);
			}
		},
		visibleColumns['weight'] && {
			title: 'Weight',
			dataIndex: 'weight',
			key: 'weight',
			sortable: true,
			width: 100,
			render: (text: string, record: ExtendedIndicatorModel) => {
				return (
					<Weight
						style={{ marginRight: '0.5rem' }}
						onClick={(values: any) => {
							let newWeight = record.weight
								? record.weight + 1
								: 1;
							if (newWeight > 3) newWeight = 0;
							props.onUpdateIndicator(record.id, {
								...record,
								weight: newWeight
							});
						}}
						disabled={!!props.scenario?.id}
					>
						{record.weight}
					</Weight>
				);
			}
		}
	]);

	return (
		<div className="IndicatorsList d-flex w-100 animation-fade-in">
			<HorizontalVulnerabilityScale
				vulnerabilities={vulnerabilities}
				scenarios={props.scenarios}
				scenario={props.scenario}
			/>

			<div className="mb-3 mt-4 w-100 pr-2 pl-2" style={{ maxWidth: '1400px' }}>
				<div className="scroll-container" style={{ overflowY: 'auto', width: '100%' }}>
					<Table
						pagination={false}
						dataSource={alteredIndicators}
						defaultSortOrder={["ascend"]}

						columns={columns}
						size="small"
						rowKey="id"
						className="mr-1"
					/>
				</div>
			</div>
		</div>
	);
};
